import React from 'react';
import {
  Chip,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  Radar as RadarIcon,
  TrendingUp,
  Warning,
} from '@mui/icons-material';
import { formatCurrency } from './KPISummaryCards';

/**
 * A single metric row with label, value, and optional progress bar.
 */
const MetricRow = ({ label, value, subtext, progress, color = '#6366f1' }) => (
  <div className="py-3 border-b border-gray-50 last:border-b-0">
    <div className="flex items-center justify-between mb-1">
      <span className="text-sm text-gray-600">{label}</span>
      <span className="text-sm font-bold text-gray-900">{value}</span>
    </div>
    {progress !== undefined && (
      <LinearProgress
        variant="determinate"
        value={Math.min(progress, 100)}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: `${color}20`,
          '& .MuiLinearProgress-bar': {
            borderRadius: 3,
            backgroundColor: color,
          },
        }}
      />
    )}
    {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
  </div>
);

/**
 * ExposureRadarPanel
 *
 * Shows:
 *   - Total Sanctioned
 *   - Total Disbursed
 *   - Outstanding
 *   - NPA Risk %
 */
const ExposureRadarPanel = ({ data = {}, loading = false }) => {
  const totalSanctioned = data.totalSanctioned ?? 0;
  const totalDisbursed = data.totalDisbursed ?? 0;
  const outstanding = data.outstanding ?? 0;
  const npaRiskPercent = data.npaRiskPercent ?? 0;

  const disbursedPercent = totalSanctioned > 0 ? (totalDisbursed / totalSanctioned) * 100 : 0;
  const outstandingPercent = totalDisbursed > 0 ? (outstanding / totalDisbursed) * 100 : 0;

  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-6 animate-pulse min-h-[280px]">
        <div className="h-5 bg-gray-200 rounded w-1/3 mb-6" />
        {[...Array(4)].map((_, i) => (
          <div key={i} className="mb-4">
            <div className="h-4 bg-gray-100 rounded w-full mb-2" />
            <div className="h-2 bg-gray-50 rounded w-full" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 hover:shadow-md transition-shadow duration-300">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center">
            <RadarIcon sx={{ fontSize: 20, color: '#6366f1' }} />
          </div>
          <h3 className="text-sm font-semibold text-gray-700">Exposure Radar</h3>
        </div>
        {npaRiskPercent > 10 && (
          <Chip
            icon={<Warning sx={{ fontSize: 14 }} />}
            label={`NPA Risk: ${npaRiskPercent.toFixed(1)}%`}
            color="error"
            size="small"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: '0.65rem', height: 24 }}
          />
        )}
      </div>

      {/* Metrics */}
      <div className="space-y-0">
        <MetricRow
          label="Total Sanctioned"
          value={formatCurrency(totalSanctioned)}
          progress={100}
          color="#6366f1"
          subtext="Approved credit limit across all applications"
        />
        <MetricRow
          label="Total Disbursed"
          value={formatCurrency(totalDisbursed)}
          progress={disbursedPercent}
          color="#0ea5e9"
          subtext={`${disbursedPercent.toFixed(1)}% of sanctioned amount`}
        />
        <MetricRow
          label="Outstanding"
          value={formatCurrency(outstanding)}
          progress={outstandingPercent}
          color="#f59e0b"
          subtext={`${outstandingPercent.toFixed(1)}% of disbursed amount`}
        />
        <MetricRow
          label="NPA Risk"
          value={`${npaRiskPercent.toFixed(1)}%`}
          progress={npaRiskPercent}
          color={npaRiskPercent > 10 ? '#ef4444' : npaRiskPercent > 5 ? '#f59e0b' : '#10b981'}
          subtext={
            npaRiskPercent > 10
              ? 'High risk — immediate attention required'
              : npaRiskPercent > 5
              ? 'Moderate risk — monitor closely'
              : 'Healthy portfolio'
          }
        />
      </div>

      {/* Summary ring */}
      <div className="mt-5 pt-4 border-t border-gray-100">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-1 text-gray-500">
            <TrendingUp sx={{ fontSize: 14, color: '#10b981' }} />
            <span>Utilization</span>
          </div>
          <Tooltip title="Disbursed / Sanctioned ratio" arrow>
            <span className="font-semibold text-gray-700">
              {disbursedPercent.toFixed(1)}%
            </span>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default ExposureRadarPanel;
